// ── HRAdvancedTabs.tsx ────────────────────────────────────────────────────────
// 3 advanced HR tabs — live data from NEXUS360_Production_Data Google Sheet
// Required tabs: hr_payroll_reconciliation | hr_agents_fictifs | hr_gpec_simulation
// Sheet ID: 1d-CCpXlFChyyq8QB456-M8auay96-bNf_HpIeYVZM0A (must be public Viewer)
//
// Usage in HRIntelligenceV2.tsx:
//   import { HRAdvancedTabs } from './HRAdvancedTabs';
//   <HRAdvancedTabs />   ← just before the Footer div
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Users, Shield, AlertTriangle, CheckCircle, Clock, TrendingUp,
  RefreshCw, Search, Filter, AlertCircle, DollarSign, Brain,
  ChevronDown, ChevronUp, Target, Award, Calendar, BarChart2, Zap
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, Cell, ComposedChart, Line, LineChart,
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter, ZAxis, ReferenceLine
} from 'recharts';
import CountUp from 'react-countup';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// ── Config ────────────────────────────────────────────────────────────────────
const MAIN_SHEET = '1d-CCpXlFChyyq8QB456-M8auay96-bNf_HpIeYVZM0A';
const sheetUrl = (tab: string) =>
  `https://docs.google.com/spreadsheets/d/${MAIN_SHEET}/gviz/tq?tqx=out:csv&sheet=${encodeURIComponent(tab)}`;

const PAL = ['#3b82f6','#10b981','#f97316','#a855f7','#eab308','#ef4444','#06b6d4','#ec4899','#84cc16'];

// ── CSV parser ────────────────────────────────────────────────────────────────
function parseCsv(text: string): Record<string,string>[] {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map(h => h.replace(/^"|"$/g,'').trim());
  return lines.slice(1).map(line => {
    const vals: string[] = [];
    let cur = '', inQ = false;
    for (const c of line) {
      if (c === '"') { inQ = !inQ; continue; }
      if (c === ',' && !inQ) { vals.push(cur); cur = ''; continue; }
      cur += c;
    }
    vals.push(cur);
    const row: Record<string,string> = {};
    headers.forEach((h,i) => { row[h] = (vals[i]||'').trim(); });
    return row;
  });
}

function useSheet<T>(tab: string, transform: (rows: Record<string,string>[]) => T[]) {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const fetch_ = useCallback(async () => {
    setLoading(true); setError(false);
    try {
      const res = await fetch(sheetUrl(tab));
      const text = await res.text();
      setData(transform(parseCsv(text)));
    } catch { setError(true); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { fetch_(); }, [fetch_]);
  return { data, loading, error, refetch: fetch_ };
}

const n = (v: any, fb = 0) => { const x = Number(v); return isNaN(x) ? fb : x; };
const fmtF = (v: number) => v >= 1e9 ? (v/1e9).toFixed(1)+'B F' : v >= 1e6 ? (v/1e6).toFixed(1)+'M F' : v >= 1e3 ? (v/1e3).toFixed(0)+'K F' : v+' F';

// ── Shared UI ─────────────────────────────────────────────────────────────────
const NexTip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <motion.div initial={{ opacity:0, scale:.95 }} animate={{ opacity:1, scale:1 }} transition={{ duration:.12 }}
      className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 shadow-2xl text-xs backdrop-blur min-w-36">
      <p className="text-white font-semibold mb-1.5 pb-1 border-b border-slate-700">{label}</p>
      {payload.map((p:any,i:number) => (
        <div key={i} className="flex justify-between gap-3 py-0.5">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-sm" style={{ background: p.fill||p.stroke||p.color }}/>
            <span className="text-slate-300">{p.name}</span>
          </div>
          <span className="font-bold" style={{ color: p.fill||p.stroke||p.color }}>{p.value}</span>
        </div>
      ))}
    </motion.div>
  );
};

const Card = ({ icon:Icon, title, children, accent='text-blue-400', className='', action }:any) => (
  <motion.div whileHover={{ boxShadow:'0 0 24px rgba(59,130,246,0.07)' }}
    className={`rounded-xl border border-blue-900/40 overflow-hidden shadow-lg ${className}`}
    style={{ background:'linear-gradient(135deg,rgba(15,23,42,0.9) 0%,rgba(30,41,59,0.65) 100%)', backdropFilter:'blur(8px)' }}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-blue-900/40"
      style={{ background:'linear-gradient(90deg,rgba(30,41,59,0.85) 0%,rgba(15,23,42,0.5) 100%)' }}>
      <Icon className={`w-4 h-4 ${accent}`}/><span className="text-sm font-semibold text-white flex-1">{title}</span>{action}
    </div>
    <div className="p-4">{children}</div>
  </motion.div>
);

const KpiCard = ({ icon:Icon, label, val, suffix='', dec=0, color, bg, border, alert=false }:any) => {
  const glow = color.includes('green')?'rgba(16,185,129,.2)':color.includes('red')?'rgba(239,68,68,.2)':
    color.includes('purple')?'rgba(168,85,247,.2)':color.includes('yellow')?'rgba(234,179,8,.2)':'rgba(59,130,246,.2)';
  return (
    <motion.div whileHover={{ scale:1.04, boxShadow:`0 0 16px ${glow}` }} transition={{ duration:.15 }}
      className={`rounded-xl p-3 border ${border} ${bg} flex items-center gap-3 cursor-default relative overflow-hidden`}
      style={{ backdropFilter:'blur(8px)', background:'linear-gradient(135deg,rgba(15,23,42,.85) 0%,rgba(30,41,59,.6) 100%)' }}>
      <motion.div className="absolute top-0 left-0 right-0 h-px opacity-40"
        style={{ background:`linear-gradient(90deg,transparent,${color.includes('green')?'#10b981':color.includes('red')?'#ef4444':color.includes('purple')?'#a855f7':color.includes('yellow')?'#eab308':'#3b82f6'},transparent)` }}
        animate={{ x:['-100%','100%'] }} transition={{ duration:2.5, repeat:Infinity, ease:'linear', repeatDelay:1 }}/>
      <Icon className={`w-5 h-5 ${color} flex-shrink-0 ${alert&&val>0?'animate-pulse':''}`}/>
      <div>
        <p className={`font-bold text-lg leading-none ${color}`}>
          <CountUp end={val} duration={1.5} decimals={dec} suffix={suffix} preserveValue separator=" "/>
        </p>
        <p className="text-slate-400 text-xs mt-0.5">{label}</p>
      </div>
      {alert&&val>0&&<motion.div className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-red-400"
        animate={{ opacity:[1,.2,1], scale:[1,1.5,1] }} transition={{ duration:1, repeat:Infinity }}/>}
    </motion.div>
  );
};

const PBar = ({ value, color='bg-blue-500', delay=0 }:{value:number;color?:string;delay?:number}) => (
  <div className="w-full bg-slate-700 rounded-full h-1.5 overflow-hidden">
    <motion.div className={`h-1.5 rounded-full ${color}`}
      initial={{ width:0 }} animate={{ width:`${Math.min(100,value)}%` }}
      transition={{ duration:1, ease:'easeOut', delay }}/>
  </div>
);

const LoadingBlock = () => (
  <div className="space-y-3 p-2">
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={40} className="mb-2"/>
    <Skeleton baseColor="#1e293b" highlightColor="#334155" height={20} count={5}/>
  </div>
);

const ErrorBlock = ({ onRetry }:{onRetry:()=>void}) => (
  <div className="flex flex-col items-center gap-3 py-10 text-slate-400">
    <AlertCircle className="w-8 h-8 text-red-400"/>
    <p className="text-sm">Impossible de charger depuis Google Sheets.</p>
    <button onClick={onRetry} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-300 text-xs hover:bg-blue-500/30">
      <RefreshCw className="w-3.5 h-3.5"/>Réessayer
    </button>
  </div>
);

// ══════════════════════════════════════════════════════════════════════════════
// TAB 1 — TRIPLE RAPPROCHEMENT PAIE + BLOCAGE AUTOMATIQUE
// ══════════════════════════════════════════════════════════════════════════════
interface PayRow {
  employee_id:string; matricule:string; full_name:string; region:string; unit:string;
  rank:string; corps:string; mois_paie:string; salaire_base_fcfa:number;
  indemnites_calculees_fcfa:number; indemnites_versees_fcfa:number; ecart_indemnites_fcfa:number;
  unite_existante_confirmee:string; presence_validee_commandant:string; indemnites_conformes:string;
  rapprochement_global_pct:number; statut_paie:string; motif_blocage:string;
  date_verification:string; verificateur:string;
}
const transformPay = (rows: Record<string,string>[]): PayRow[] =>
  rows.map(r => ({...r, salaire_base_fcfa:n(r.salaire_base_fcfa),
    indemnites_calculees_fcfa:n(r.indemnites_calculees_fcfa),
    indemnites_versees_fcfa:n(r.indemnites_versees_fcfa),
    ecart_indemnites_fcfa:n(r.ecart_indemnites_fcfa),
    rapprochement_global_pct:n(r.rapprochement_global_pct)} as PayRow));

const TripleRapprochement = () => {
  const { data, loading, error, refetch } = useSheet('hr_payroll_reconciliation', transformPay);
  const [search, setSearch] = useState('');
  const [statutF, setStatutF] = useState('');
  const [moisF, setMoisF] = useState('');

  const filtered = useMemo(() => data.filter(r =>
    (!statutF || r.statut_paie === statutF) &&
    (!moisF   || r.mois_paie === moisF) &&
    (!search  || [r.full_name, r.matricule, r.unit].join(' ').toLowerCase().includes(search.toLowerCase()))
  ), [data, statutF, moisF, search]);

  const valides  = data.filter(r => r.statut_paie === 'VALIDÉ').length;
  const bloques  = data.filter(r => r.statut_paie === 'BLOQUÉ').length;
  const tauxRecon= data.length ? Math.round(valides/data.length*100*10)/10 : 0;
  const totalEcart = data.reduce((s,r) => s+r.ecart_indemnites_fcfa, 0);
  const moisList = [...new Set(data.map(r => r.mois_paie))].sort();

  // Chart: rapprochement by motif
  const motifStats = useMemo(() => {
    const m: Record<string,number> = {};
    data.filter(r=>r.motif_blocage).forEach(r => { m[r.motif_blocage]=(m[r.motif_blocage]||0)+1; });
    return Object.entries(m).map(([name,count])=>({name:name.slice(0,30),count})).sort((a,b)=>b.count-a.count);
  }, [data]);

  // Rapport 3 checks
  const check1 = data.filter(r=>r.unite_existante_confirmee==='OUI').length;
  const check2 = data.filter(r=>r.presence_validee_commandant==='OUI').length;
  const check3 = data.filter(r=>r.indemnites_conformes==='OUI').length;

  if (loading) return <LoadingBlock/>;
  if (error) return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={CheckCircle} label="Paies validées"        val={valides}   color="text-green-400"  bg="bg-green-500/10"  border="border-green-500/30"/>
        <KpiCard icon={AlertTriangle} label="Paies bloquées"      val={bloques}   color="text-red-400"    bg="bg-red-500/10"    border="border-red-500/30" alert/>
        <KpiCard icon={TrendingUp}  label="Taux rapprochement"    val={tauxRecon} suffix="%" dec={1} color="text-blue-400" bg="bg-blue-500/10" border="border-blue-500/30"/>
        <KpiCard icon={DollarSign}  label="Total écarts indemnités" val={Math.round(totalEcart/1e6)} suffix="M F" color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30"/>
      </div>

      {/* Blocage alert */}
      {bloques > 0 && (
        <motion.div className="rounded-xl border border-red-500/40 p-3 flex items-start gap-3 relative overflow-hidden"
          style={{ background:'linear-gradient(135deg,rgba(127,29,29,.2) 0%,rgba(15,23,42,.8) 100%)' }}>
          <motion.div className="absolute inset-0 rounded-xl pointer-events-none"
            animate={{ boxShadow:['0 0 0px rgba(239,68,68,0)','0 0 20px rgba(239,68,68,.15)','0 0 0px rgba(239,68,68,0)'] }}
            transition={{ duration:2, repeat:Infinity }}/>
          <motion.div animate={{ rotate:[0,-8,8,-8,0] }} transition={{ duration:.5, repeat:Infinity, repeatDelay:3 }}>
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0"/>
          </motion.div>
          <div className="flex-1 relative z-10">
            <p className="text-red-300 font-bold text-sm mb-1">
              ⛔ {bloques} paie(s) bloquée(s) — Rapprochement &lt; 99.5% · Rapport transmis au DAF et au DG
            </p>
            <div className="flex flex-wrap gap-2">
              {data.filter(r=>r.statut_paie==='BLOQUÉ').slice(0,5).map((r,i)=>(
                <motion.span key={i} className="text-xs bg-red-500/20 text-red-300 px-2 py-0.5 rounded-full border border-red-500/30"
                  animate={{ opacity:[1,.6,1] }} transition={{ duration:1.5, repeat:Infinity, delay:i*.2 }}>
                  {r.full_name} · {r.motif_blocage}
                </motion.span>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* 3 checks gauge */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[
          { label:'① Unité existante confirmée', ok:check1, icon:Shield, color:'text-blue-400' },
          { label:'② Présence validée commandant', ok:check2, icon:CheckCircle, color:'text-green-400' },
          { label:'③ Indemnités = Droits calculés', ok:check3, icon:DollarSign, color:'text-purple-400' },
        ].map(({label,ok,icon:Icon,color},i) => {
          const pct = data.length ? Math.round(ok/data.length*100) : 0;
          const pass = pct >= 99.5;
          return (
            <motion.div key={i} whileHover={{ scale:1.02 }}
              className={`rounded-xl border p-4 ${pass?'border-green-500/30 bg-green-500/10':'border-orange-500/30 bg-orange-500/10'}`}
              style={{ backdropFilter:'blur(8px)' }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon className={`w-4 h-4 ${color}`}/>
                  <span className="text-white text-xs font-medium">{label}</span>
                </div>
                <motion.span className={`text-lg font-black ${pass?'text-green-400':'text-orange-400'}`}
                  animate={{ opacity:[1,.7,1] }} transition={{ duration:2, repeat:Infinity }}>
                  <CountUp end={pct} duration={1.5} suffix="%" preserveValue/>
                </motion.span>
              </div>
              <PBar value={pct} color={pass?'bg-green-500':'bg-orange-500'}/>
              <p className="text-slate-500 text-xs mt-2">{ok}/{data.length} agents conformes · Seuil: 99.5%</p>
            </motion.div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Motifs de blocage */}
        <Card icon={BarChart2} title="Motifs de blocage paie" accent="text-red-400">
          {motifStats.length === 0 ? (
            <p className="text-green-400 text-sm text-center py-8">✓ Aucun blocage détecté</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={motifStats} layout="vertical" margin={{top:4,right:8,left:8,bottom:4}} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
                <XAxis type="number" tick={{ fill:'#94a3b8', fontSize:9 }} allowDecimals={false}/>
                <YAxis type="category" dataKey="name" tick={{ fill:'#cbd5e1', fontSize:8 }} width={180}/>
                <Tooltip content={<NexTip/>}/>
                <Bar dataKey="count" name="Cas" radius={[0,3,3,0]} isAnimationActive animationDuration={800}>
                  {motifStats.map((_,i)=><Cell key={i} fill={i===0?'#ef4444':i===1?'#f97316':i===2?'#eab308':'#3b82f6'}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        {/* Rapprochement par corps */}
        <Card icon={Users} title="Taux rapprochement par corps" accent="text-blue-400">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={[...new Set(data.map(r=>r.corps))].map(corps => {
              const sub = data.filter(r=>r.corps===corps);
              return { corps: corps.replace('Nationale','Nat.').replace('Républicaine','Rép.'),
                ok: sub.filter(r=>r.statut_paie==='VALIDÉ').length,
                bloque: sub.filter(r=>r.statut_paie==='BLOQUÉ').length };
            })} margin={{top:4,right:8,left:0,bottom:4}} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
              <XAxis dataKey="corps" tick={{ fill:'#94a3b8', fontSize:9 }}/>
              <YAxis tick={{ fill:'#94a3b8', fontSize:9 }} allowDecimals={false}/>
              <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{ fontSize:'9px' }}/>
              <Bar dataKey="ok"     name="Validé"  fill="#10b981" radius={[2,2,0,0]} isAnimationActive animationDuration={900}/>
              <Bar dataKey="bloque" name="Bloqué"  fill="#ef4444" radius={[2,2,0,0]} isAnimationActive animationDuration={1100}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Filters + Table */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{ background:'linear-gradient(135deg,rgba(15,23,42,.8) 0%,rgba(30,41,59,.5) 100%)', backdropFilter:'blur(8px)' }}>
        <Filter className="w-4 h-4 text-blue-400"/>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Nom / matricule…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-44 focus:outline-none focus:border-blue-500/60"/>
        </div>
        <select value={statutF} onChange={e=>setStatutF(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
          <option value="">Statut</option><option>VALIDÉ</option><option>BLOQUÉ</option>
        </select>
        <select value={moisF} onChange={e=>setMoisF(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
          <option value="">Mois</option>{moisList.map(m=><option key={m}>{m}</option>)}
        </select>
        {(search||statutF||moisF)&&<button onClick={()=>{setSearch('');setStatutF('');setMoisF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} agents</span>
      </div>

      <Card icon={Users} title="Registre triple rapprochement — Google Sheets live" accent="text-blue-400">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{ maxHeight:'420px' }}>
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['Matricule','Nom','Grade','Région','Unité','Mois','Salaire','Ind. calculées','Ind. versées','Écart','①Unité','②Présence','③Indemnités','Score %','Statut','Motif'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r,i)=>(
                  <motion.tr key={r.employee_id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:i*.01 }}
                    whileHover={{ backgroundColor:'rgba(59,130,246,.05)' }}
                    className={`border-b border-blue-900/30 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.matricule}</td>
                    <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{r.full_name}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap text-xs">{r.rank.replace('Commissaire de Police','Commissaire').replace('Gardien de la Paix','Gardien').replace('Inspecteur de Police','Inspecteur').replace('Officier de Police','Officier')}</td>
                    <td className="px-3 py-2 text-slate-300">{r.region}</td>
                    <td className="px-3 py-2 text-slate-300 max-w-40 truncate" title={r.unit}>{r.unit}</td>
                    <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{r.mois_paie}</td>
                    <td className="px-3 py-2 text-blue-300 text-right whitespace-nowrap">{fmtF(r.salaire_base_fcfa)}</td>
                    <td className="px-3 py-2 text-green-400 text-right whitespace-nowrap">{fmtF(r.indemnites_calculees_fcfa)}</td>
                    <td className="px-3 py-2 text-purple-400 text-right whitespace-nowrap">{fmtF(r.indemnites_versees_fcfa)}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap"><span className={r.ecart_indemnites_fcfa>0?'text-red-400 font-bold':'text-green-400'}>{r.ecart_indemnites_fcfa>0?fmtF(r.ecart_indemnites_fcfa):'—'}</span></td>
                    {[r.unite_existante_confirmee,r.presence_validee_commandant,r.indemnites_conformes].map((v,j)=>(
                      <td key={j} className="px-3 py-2 text-center">
                        <span className={v==='OUI'?'text-green-400 font-bold':'text-red-400 font-bold'}>{v==='OUI'?'✓':'✗'}</span>
                      </td>
                    ))}
                    <td className="px-3 py-2 text-center">
                      <span className={r.rapprochement_global_pct>=99.5?'text-green-400':'text-red-400 font-bold'}>{r.rapprochement_global_pct}%</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${r.statut_paie==='VALIDÉ'?'bg-green-500/10 border-green-500/30 text-green-400':'bg-red-500/10 border-red-500/30 text-red-400'}`}
                        animate={r.statut_paie==='BLOQUÉ'?{opacity:[1,.6,1]}:{}} transition={{ duration:1.5, repeat:Infinity }}>
                        {r.statut_paie}
                      </motion.span>
                    </td>
                    <td className="px-3 py-2 text-slate-400 max-w-48 truncate text-xs">{r.motif_blocage||'—'}</td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TAB 2 — ML DÉTECTION AGENTS FICTIFS
// ══════════════════════════════════════════════════════════════════════════════
interface FictifRow {
  employee_id:string; matricule:string; full_name:string; region:string; unit:string;
  rank:string; corps:string; date_enrolement:string; pattern_enrolement:string;
  jours_sans_presence_physique:number; doublon_biometrique:string; matricule_doublon:string;
  derniere_presence_physique:string; salaire_percu_sans_presence_fcfa:number;
  score_fictif_ml:number; categorie_risque:string; statut_investigation:string;
  recommandation:string; date_analyse:string;
}
const transformFictif = (rows: Record<string,string>[]): FictifRow[] =>
  rows.map(r => ({...r,
    jours_sans_presence_physique:n(r.jours_sans_presence_physique),
    salaire_percu_sans_presence_fcfa:n(r.salaire_percu_sans_presence_fcfa),
    score_fictif_ml:n(r.score_fictif_ml)} as FictifRow));

const AgentsFictifs = () => {
  const { data, loading, error, refetch } = useSheet('hr_agents_fictifs', transformFictif);
  const [riskF, setRiskF] = useState('');
  const [search, setSearch] = useState('');
  const [sortDesc, setSortDesc] = useState(true);

  const filtered = useMemo(() => [...data]
    .filter(r => (!riskF||r.categorie_risque===riskF) && (!search||[r.full_name,r.matricule].join(' ').toLowerCase().includes(search.toLowerCase())))
    .sort((a,b) => sortDesc ? b.score_fictif_ml-a.score_fictif_ml : a.score_fictif_ml-b.score_fictif_ml),
  [data, riskF, search, sortDesc]);

  const confirmes = data.filter(r=>r.categorie_risque==='CONFIRME').length;
  const eleves    = data.filter(r=>r.categorie_risque==='ELEVE').length;
  const totalSal  = data.reduce((s,r)=>s+r.salaire_percu_sans_presence_fcfa,0);
  const doublons  = data.filter(r=>r.doublon_biometrique==='OUI').length;

  const riskChartData = ['CONFIRME','ELEVE','MODERE','FAIBLE'].map(cat => ({
    cat, count:data.filter(r=>r.categorie_risque===cat).length,
    fill:cat==='CONFIRME'?'#ef4444':cat==='ELEVE'?'#f97316':cat==='MODERE'?'#eab308':'#10b981'
  }));

  const scatterData = data.map(r => ({
    x: r.jours_sans_presence_physique,
    y: r.score_fictif_ml,
    name: r.full_name,
    risk: r.categorie_risque,
    fill: r.categorie_risque==='CONFIRME'?'#ef4444':r.categorie_risque==='ELEVE'?'#f97316':r.categorie_risque==='MODERE'?'#eab308':'#10b981'
  }));

  if (loading) return <LoadingBlock/>;
  if (error) return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* Header alert */}
      <div className="rounded-xl border border-purple-500/30 p-3 flex items-center gap-3"
        style={{ background:'linear-gradient(135deg,rgba(88,28,135,.2) 0%,rgba(15,23,42,.8) 100%)' }}>
        <Brain className="w-5 h-5 text-purple-400 flex-shrink-0"/>
        <div>
          <p className="text-white font-semibold text-sm">Modèle ML — Détection agents fictifs · Analyse patterns enrôlement + biométrie + présence</p>
          <p className="text-slate-400 text-xs">Régression logistique sur: jours_sans_présence · doublon_biométrique · pattern_enrôlement · matricule_séquentiel</p>
        </div>
        <div className="ml-auto text-right">
          <motion.p className="text-purple-300 font-black text-2xl" animate={{ opacity:[1,.7,1] }} transition={{ duration:2, repeat:Infinity }}>
            <CountUp end={confirmes+eleves} duration={1.5} preserveValue/>
          </motion.p>
          <p className="text-slate-400 text-xs">Agents à risque</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={AlertTriangle} label="Agents confirmés fictifs" val={confirmes} color="text-red-400"    bg="bg-red-500/10"    border="border-red-500/30" alert/>
        <KpiCard icon={AlertCircle}   label="Risque élevé"             val={eleves}    color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30" alert/>
        <KpiCard icon={DollarSign}    label="Salaires perçus sans présence" val={Math.round(totalSal/1e6)} suffix="M F" color="text-yellow-400" bg="bg-yellow-500/10" border="border-yellow-500/30"/>
        <KpiCard icon={Users}         label="Doublons biométriques"    val={doublons}  color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/30" alert/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Risk distribution */}
        <Card icon={BarChart2} title="Distribution du risque ML" accent="text-purple-400">
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={riskChartData} margin={{top:8,right:8,left:0,bottom:4}} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
              <XAxis dataKey="cat" tick={{ fill:'#94a3b8', fontSize:10 }}/>
              <YAxis tick={{ fill:'#94a3b8', fontSize:9 }} allowDecimals={false}/>
              <Tooltip content={<NexTip/>}/>
              <Bar dataKey="count" name="Agents" radius={[3,3,0,0]} isAnimationActive animationDuration={1000}>
                {riskChartData.map((d,i)=><Cell key={i} fill={d.fill}/>)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Scatter: jours_absence vs score */}
        <Card icon={Target} title="Absence physique vs Score ML fictif" accent="text-red-400">
          <ResponsiveContainer width="100%" height={240}>
            <ScatterChart margin={{top:8,right:16,left:0,bottom:16}}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" strokeOpacity={.6}/>
              <XAxis type="number" dataKey="x" name="Jours sans présence" tick={{ fill:'#94a3b8',fontSize:9 }} label={{ value:'Jours sans présence', position:'insideBottom', offset:-8, fill:'#64748b', fontSize:9 }}/>
              <YAxis type="number" dataKey="y" name="Score ML" domain={[0,100]} tick={{ fill:'#94a3b8',fontSize:9 }} label={{ value:'Score ML', angle:-90, position:'insideLeft', fill:'#64748b', fontSize:9 }}/>
              <ZAxis range={[40,160]}/>
              <Tooltip cursor={{ stroke:'rgba(99,102,241,.3)', strokeWidth:1 }} content={({ active, payload }:any) => {
                if (!active||!payload?.length) return null;
                const d = payload[0]?.payload;
                return (<div className="bg-slate-900/95 border border-slate-600 rounded-xl p-3 text-xs backdrop-blur"><p className="text-white font-bold mb-1">{d.name}</p><p style={{color:d.fill}}>Risque: {d.risk}</p><p className="text-slate-300">Absence: {d.x}j · Score: {d.y}</p></div>);
              }}/>
              <ReferenceLine y={55} stroke="#f97316" strokeDasharray="4 2" label={{ value:'Risque élevé', fill:'#f97316', fontSize:8, position:'insideTopRight' }}/>
              <ReferenceLine y={80} stroke="#ef4444" strokeDasharray="4 2" label={{ value:'Confirmé', fill:'#ef4444', fontSize:8, position:'insideTopRight' }}/>
              <ReferenceLine x={90} stroke="#eab308" strokeDasharray="4 2" label={{ value:'>90j', fill:'#eab308', fontSize:8, position:'insideTopLeft' }}/>
              <Scatter data={scatterData} shape={(props:any) => {
                const { cx,cy,payload } = props;
                return (<g><circle cx={cx} cy={cy} r={7} fill={payload.fill} fillOpacity={.8} stroke="#0f172a" strokeWidth={1.5}/><circle cx={cx} cy={cy} r={3} fill="white" fillOpacity={.4}/></g>);
              }} isAnimationActive animationDuration={1400}/>
            </ScatterChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{ background:'linear-gradient(135deg,rgba(15,23,42,.8) 0%,rgba(30,41,59,.5) 100%)', backdropFilter:'blur(8px)' }}>
        <Filter className="w-4 h-4 text-blue-400"/>
        <div className="relative">
          <Search className="w-3 h-3 text-slate-400 absolute left-2 top-1/2 -translate-y-1/2"/>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Nom / matricule…"
            className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-white pl-6 pr-3 py-1 w-44 focus:outline-none focus:border-blue-500/60"/>
        </div>
        <select value={riskF} onChange={e=>setRiskF(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
          <option value="">Risque</option>{['CONFIRME','ELEVE','MODERE','FAIBLE'].map(r=><option key={r}>{r}</option>)}
        </select>
        <button onClick={()=>setSortDesc(s=>!s)} className="flex items-center gap-1 text-xs text-slate-400 hover:text-white">
          Score ML {sortDesc?<ChevronDown className="w-3 h-3"/>:<ChevronUp className="w-3 h-3"/>}
        </button>
        {(search||riskF)&&<button onClick={()=>{setSearch('');setRiskF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} agents</span>
      </div>

      <Card icon={Shield} title="Registre ML agents fictifs — Google Sheets live" accent="text-purple-400">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{ maxHeight:'400px' }}>
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['Matricule','Nom','Grade','Région','Pattern enrôlement','Jours absence','Doublon bio.','Dernière présence','Salaire sans présence','Score ML','Risque','Statut','Recommandation'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r,i) => {
                  const riskColor = r.categorie_risque==='CONFIRME'?'text-red-400':r.categorie_risque==='ELEVE'?'text-orange-400':r.categorie_risque==='MODERE'?'text-yellow-400':'text-green-400';
                  const riskBg   = r.categorie_risque==='CONFIRME'?'bg-red-500/10 border-red-500/30':r.categorie_risque==='ELEVE'?'bg-orange-500/10 border-orange-500/30':r.categorie_risque==='MODERE'?'bg-yellow-500/10 border-yellow-500/30':'bg-green-500/10 border-green-500/30';
                  return (
                    <motion.tr key={r.employee_id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:i*.01 }}
                      whileHover={{ backgroundColor:'rgba(59,130,246,.05)' }}
                      className={`border-b border-blue-900/30 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'}`}>
                      <td className="px-3 py-2 text-slate-400 font-mono">{r.matricule}</td>
                      <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{r.full_name}</td>
                      <td className="px-3 py-2 text-slate-300 whitespace-nowrap text-xs">{r.rank.replace('Commissaire de Police','Commissaire').replace('Gardien de la Paix','Gardien').replace('Inspecteur de Police','Inspecteur').replace('Officier de Police','Officier')}</td>
                      <td className="px-3 py-2 text-slate-300">{r.region}</td>
                      <td className="px-3 py-2 text-slate-300 max-w-44 truncate">{r.pattern_enrolement}</td>
                      <td className="px-3 py-2 text-center"><span className={r.jours_sans_presence_physique>90?'text-red-400 font-bold':r.jours_sans_presence_physique>30?'text-orange-400':'text-green-400'}>{r.jours_sans_presence_physique}j</span></td>
                      <td className="px-3 py-2 text-center"><span className={r.doublon_biometrique==='OUI'?'text-red-400 font-bold':'text-green-400'}>{r.doublon_biometrique==='OUI'?'OUI ⚠':'NON'}</span></td>
                      <td className="px-3 py-2 text-slate-400 font-mono whitespace-nowrap">{r.derniere_presence_physique}</td>
                      <td className="px-3 py-2 text-right"><span className={r.salaire_percu_sans_presence_fcfa>0?'text-red-400 font-bold':'text-slate-500'}>{r.salaire_percu_sans_presence_fcfa>0?fmtF(r.salaire_percu_sans_presence_fcfa):'—'}</span></td>
                      <td className="px-3 py-2 text-center">
                        <div className="relative w-10 h-10 mx-auto flex-shrink-0">
                          <svg viewBox="0 0 36 36" className="-rotate-90 w-10 h-10">
                            <circle cx="18" cy="18" r="14" fill="none" stroke="#1e293b" strokeWidth="3"/>
                            <motion.circle cx="18" cy="18" r="14" fill="none"
                              stroke={r.categorie_risque==='CONFIRME'?'#ef4444':r.categorie_risque==='ELEVE'?'#f97316':r.categorie_risque==='MODERE'?'#eab308':'#10b981'}
                              strokeWidth="3" initial={{ strokeDasharray:'0 88' }}
                              animate={{ strokeDasharray:`${r.score_fictif_ml*.88} 88` }}
                              transition={{ duration:1.2, ease:'easeOut', delay:i*.02 }} strokeLinecap="round"/>
                          </svg>
                          <span className="absolute inset-0 flex items-center justify-center text-white text-xs font-bold">{Math.round(r.score_fictif_ml)}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <motion.span className={`px-2 py-0.5 rounded-full text-xs font-bold border ${riskBg} ${riskColor}`}
                          animate={r.categorie_risque==='CONFIRME'?{opacity:[1,.7,1]}:{}} transition={{ duration:1.5, repeat:Infinity }}>
                          {r.categorie_risque}
                        </motion.span>
                      </td>
                      <td className="px-3 py-2 text-slate-400 whitespace-nowrap">{r.statut_investigation}</td>
                      <td className="px-3 py-2 text-slate-300 max-w-56 truncate text-xs" title={r.recommandation}>{r.recommandation}</td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// TAB 3 — GPEC 5 ANS + PYRAMIDE GRADES/CORPS
// ══════════════════════════════════════════════════════════════════════════════
interface GpecRow {
  employee_id:string; matricule:string; full_name:string; region:string; unit:string;
  rank:string; corps:string; annee_projection:number; annees_service_actuelles:number;
  age_actuel:number; date_retraite_prevue:string; depart_retraite_dans_n_ans:number;
  effectif_corps_region_actuel:number; departs_retraite_prevus_5ans:number;
  besoins_recrutement_5ans:number; promotions_previsibles_5ans:number;
  effectif_projete_2030:number; cout_masse_salariale_actuelle_fcfa:number;
  cout_projete_2030_fcfa:number; taux_encadrement_actuel:number;
  taux_encadrement_projete:number; deficit_critique:string; priorite_recrutement:string;
}
const transformGpec = (rows: Record<string,string>[]): GpecRow[] =>
  rows.map(r => ({...r,
    annee_projection:n(r.annee_projection), annees_service_actuelles:n(r.annees_service_actuelles),
    age_actuel:n(r.age_actuel), depart_retraite_dans_n_ans:n(r.depart_retraite_dans_n_ans),
    effectif_corps_region_actuel:n(r.effectif_corps_region_actuel),
    departs_retraite_prevus_5ans:n(r.departs_retraite_prevus_5ans),
    besoins_recrutement_5ans:n(r.besoins_recrutement_5ans),
    promotions_previsibles_5ans:n(r.promotions_previsibles_5ans),
    effectif_projete_2030:n(r.effectif_projete_2030),
    cout_masse_salariale_actuelle_fcfa:n(r.cout_masse_salariale_actuelle_fcfa),
    cout_projete_2030_fcfa:n(r.cout_projete_2030_fcfa),
    taux_encadrement_actuel:n(r.taux_encadrement_actuel),
    taux_encadrement_projete:n(r.taux_encadrement_projete)} as GpecRow));

const GpecSimulation = () => {
  const { data, loading, error, refetch } = useSheet('hr_gpec_simulation', transformGpec);
  const [corpsF, setCorpsF] = useState('');
  const [regionF, setRegionF] = useState('');

  const filtered = useMemo(() => data.filter(r =>
    (!corpsF  || r.corps   === corpsF) &&
    (!regionF || r.region  === regionF)
  ), [data, corpsF, regionF]);

  const totalDeparts   = filtered.reduce((s,r)=>s+r.departs_retraite_prevus_5ans,0);
  const totalBesoins   = filtered.reduce((s,r)=>s+r.besoins_recrutement_5ans,0);
  const totalPromos    = filtered.reduce((s,r)=>s+r.promotions_previsibles_5ans,0);
  const totalMasse     = filtered.reduce((s,r)=>s+r.cout_masse_salariale_actuelle_fcfa,0);
  const totalMasse2030 = filtered.reduce((s,r)=>s+r.cout_projete_2030_fcfa,0);
  const critiques      = filtered.filter(r=>r.deficit_critique==='OUI').length;

  // Pyramide des grades
  const RANKS_ORDER = ['Gardien de la Paix','Inspecteur de Police','Officier de Police','Commissaire de Police','Commissaire Divisionnaire'];
  const pyramideData = RANKS_ORDER.map(rank => ({
    rank: rank.replace('Commissaire Divisionnaire','Comm. Div.').replace('Commissaire de Police','Commissaire').replace('Officier de Police','Officier').replace('Inspecteur de Police','Inspecteur').replace('Gardien de la Paix','Gardien'),
    effectif: filtered.filter(r=>r.rank===rank).length,
    departs:  filtered.filter(r=>r.rank===rank).reduce((s,r)=>s+r.departs_retraite_prevus_5ans,0),
    besoins:  filtered.filter(r=>r.rank===rank).reduce((s,r)=>s+r.besoins_recrutement_5ans,0),
    masse:    Math.round(filtered.filter(r=>r.rank===rank).reduce((s,r)=>s+r.cout_masse_salariale_actuelle_fcfa,0)/1e6),
  }));

  // Corps distribution
  const corpsData = [...new Set(data.map(r=>r.corps))].map(corps => ({
    corps: corps.replace('Nationale','Nat.').replace('Républicaine','Rép.'),
    effectif: data.filter(r=>r.corps===corps).length,
    departs:  data.filter(r=>r.corps===corps).reduce((s,r)=>s+r.departs_retraite_prevus_5ans,0),
    masse:    Math.round(data.filter(r=>r.corps===corps).reduce((s,r)=>s+r.cout_masse_salariale_actuelle_fcfa,0)/1e9*10)/10,
  }));

  // Projection timeline by region
  const regionTimeline = [...new Set(filtered.map(r=>r.region))].slice(0,6).map(region => {
    const sub = filtered.filter(r=>r.region===region);
    return { region, actuel: sub.reduce((s,r)=>s+r.effectif_corps_region_actuel,0), projete2030: sub.reduce((s,r)=>s+r.effectif_projete_2030,0) };
  }).sort((a,b)=>b.actuel-a.actuel);

  const corpsList   = [...new Set(data.map(r=>r.corps))];
  const regionsList = [...new Set(data.map(r=>r.region))].sort();

  if (loading) return <LoadingBlock/>;
  if (error) return <ErrorBlock onRetry={refetch}/>;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="rounded-xl border border-cyan-500/30 p-3 flex items-center gap-3"
        style={{ background:'linear-gradient(135deg,rgba(6,182,212,.1) 0%,rgba(15,23,42,.8) 100%)' }}>
        <Calendar className="w-5 h-5 text-cyan-400 flex-shrink-0"/>
        <div>
          <p className="text-white font-semibold text-sm">GPEC 5 ans — Simulation 2025→2030 · Départs retraite · Recrutements · Promotions · Masse salariale</p>
          <p className="text-slate-400 text-xs">Projection automatisée par corps · région · grade · Identification déficits critiques</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users}      label="Départs retraite 5 ans" val={totalDeparts}  color="text-orange-400" bg="bg-orange-500/10" border="border-orange-500/30"/>
        <KpiCard icon={TrendingUp} label="Besoins recrutement"    val={totalBesoins}  color="text-blue-400"   bg="bg-blue-500/10"   border="border-blue-500/30"/>
        <KpiCard icon={Award}      label="Promotions prévisibles"  val={totalPromos}   color="text-purple-400" bg="bg-purple-500/10" border="border-purple-500/30"/>
        <KpiCard icon={AlertTriangle} label="Déficits critiques"   val={critiques}     color="text-red-400"    bg="bg-red-500/10"    border="border-red-500/30" alert/>
      </div>

      {/* Masse salariale evolution */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {[
          { label:'Masse salariale actuelle', val:Math.round(totalMasse/1e9*10)/10, suffix:'Mds F', color:'text-blue-400', bg:'bg-blue-500/10', border:'border-blue-500/30', icon:DollarSign },
          { label:'Masse salariale 2030',     val:Math.round(totalMasse2030/1e9*10)/10, suffix:'Mds F', color:'text-green-400', bg:'bg-green-500/10', border:'border-green-500/30', icon:TrendingUp },
          { label:'Évolution masse salariale', val:Math.round((totalMasse2030-totalMasse)/totalMasse*100), suffix:'%', color: totalMasse2030>totalMasse?'text-orange-400':'text-green-400', bg:'bg-orange-500/10', border:'border-orange-500/30', icon:Zap },
        ].map((k,i) => (
          <motion.div key={i} whileHover={{ scale:1.03 }}
            className={`rounded-xl p-4 border ${k.border} ${k.bg} flex items-center gap-4`}
            style={{ backdropFilter:'blur(8px)', background:'linear-gradient(135deg,rgba(15,23,42,.85) 0%,rgba(30,41,59,.6) 100%)' }}>
            <k.icon className={`w-8 h-8 ${k.color} flex-shrink-0`}/>
            <div>
              <p className={`font-black text-2xl ${k.color}`}><CountUp end={k.val} duration={1.5} decimals={1} suffix={k.suffix} preserveValue/></p>
              <p className="text-slate-400 text-xs mt-0.5">{k.label}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-blue-900/40 p-3 flex flex-wrap gap-3 items-center"
        style={{ background:'linear-gradient(135deg,rgba(15,23,42,.8) 0%,rgba(30,41,59,.5) 100%)', backdropFilter:'blur(8px)' }}>
        <Filter className="w-4 h-4 text-blue-400"/>
        {[{v:corpsF,s:setCorpsF,o:corpsList,p:'Corps'},{v:regionF,s:setRegionF,o:regionsList,p:'Région'}].map((f,i)=>(
          <select key={i} value={f.v} onChange={e=>f.s(e.target.value)} className="bg-slate-700/60 border border-slate-600/40 rounded-lg text-xs text-slate-300 px-2 py-1 focus:outline-none">
            <option value="">{f.p}</option>{f.o.map(o=><option key={o}>{o}</option>)}
          </select>
        ))}
        {(corpsF||regionF)&&<button onClick={()=>{setCorpsF('');setRegionF('');}} className="text-xs text-slate-400 hover:text-white px-2 py-1 rounded-lg border border-slate-600/40">✕</button>}
        <span className="ml-auto text-xs text-slate-500">{filtered.length} agents</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pyramide des grades */}
        <Card icon={BarChart2} title="Pyramide des grades — Effectifs · Départs · Besoins" accent="text-yellow-400">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={pyramideData} layout="vertical" margin={{top:4,right:48,left:8,bottom:4}} barCategoryGap="18%" barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
              <XAxis type="number" tick={{ fill:'#94a3b8', fontSize:9 }}/>
              <YAxis type="category" dataKey="rank" tick={{ fill:'#cbd5e1', fontSize:9 }} width={72}/>
              <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{ fontSize:'9px' }}/>
              <Bar dataKey="effectif" name="Effectif actuel" fill="#3b82f6" barSize={8} isAnimationActive animationDuration={900}/>
              <Bar dataKey="departs"  name="Départs retraite 5a" fill="#f97316" barSize={8} isAnimationActive animationDuration={1100}/>
              <Bar dataKey="besoins"  name="Besoins recrutement" fill="#10b981" barSize={8} isAnimationActive animationDuration={1300}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        {/* Projection par région */}
        <Card icon={TrendingUp} title="Projection effectifs 2025→2030 par région" accent="text-cyan-400">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={regionTimeline} layout="vertical" margin={{top:4,right:32,left:8,bottom:4}} barCategoryGap="18%" barGap={2}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" horizontal={false}/>
              <XAxis type="number" tick={{ fill:'#94a3b8', fontSize:9 }}/>
              <YAxis type="category" dataKey="region" tick={{ fill:'#cbd5e1', fontSize:9 }} width={80}/>
              <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{ fontSize:'9px' }}/>
              <Bar dataKey="actuel"    name="Effectif 2025" fill="#3b82f6" barSize={10} isAnimationActive animationDuration={900}/>
              <Bar dataKey="projete2030" name="Projeté 2030" fill="#10b981" barSize={10} isAnimationActive animationDuration={1100}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* Corps distribution */}
      <Card icon={Shield} title="Répartition par corps — Effectifs · Départs · Masse salariale (Mds F)" accent="text-blue-400">
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={corpsData} margin={{top:4,right:8,left:0,bottom:4}} barCategoryGap="30%">
            <CartesianGrid strokeDasharray="3 3" stroke="#1e3a5f" vertical={false}/>
            <XAxis dataKey="corps" tick={{ fill:'#94a3b8', fontSize:10 }}/>
            <YAxis tick={{ fill:'#94a3b8', fontSize:9 }} allowDecimals={false}/>
            <Tooltip content={<NexTip/>}/><Legend wrapperStyle={{ fontSize:'9px' }}/>
            <Bar dataKey="effectif" name="Effectif" fill="#3b82f6" radius={[2,2,0,0]} isAnimationActive animationDuration={800}/>
            <Bar dataKey="departs"  name="Départs 5a" fill="#f97316" radius={[2,2,0,0]} isAnimationActive animationDuration={1000}/>
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Full GPEC table */}
      <Card icon={Calendar} title="Tableau GPEC complet — Google Sheets live" accent="text-cyan-400">
        <div className="overflow-x-auto">
          <div className="overflow-y-auto" style={{ maxHeight:'380px' }}>
            <table className="w-full text-xs min-w-max">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-blue-900/60 bg-slate-700">
                  {['Matricule','Nom','Grade','Corps','Région','Âge','Ans service','Retraite prévue','Dans N ans','Effectif actuel','Départs 5a','Besoins recr.','Promos 5a','Eff. 2030','Masse act. (M F)','Masse 2030 (M F)','Déficit','Priorité'].map(h=>(
                    <th key={h} className="px-3 py-2.5 text-left text-blue-300 font-semibold whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r,i) => (
                  <motion.tr key={r.employee_id} initial={{ opacity:0 }} animate={{ opacity:1 }} transition={{ delay:i*.01 }}
                    whileHover={{ backgroundColor:'rgba(59,130,246,.05)' }}
                    className={`border-b border-blue-900/30 transition-colors ${i%2===0?'bg-slate-800/40':'bg-slate-700/30'} ${r.deficit_critique==='OUI'?'border-l-2 border-l-red-500/50':''}`}>
                    <td className="px-3 py-2 text-slate-400 font-mono">{r.matricule}</td>
                    <td className="px-3 py-2 text-white font-medium whitespace-nowrap">{r.full_name}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap text-xs">{r.rank.replace('Commissaire de Police','Commissaire').replace('Gardien de la Paix','Gardien').replace('Inspecteur de Police','Inspecteur').replace('Officier de Police','Officier')}</td>
                    <td className="px-3 py-2 text-slate-300 whitespace-nowrap">{r.corps}</td>
                    <td className="px-3 py-2 text-slate-300">{r.region}</td>
                    <td className="px-3 py-2 text-center text-slate-300">{r.age_actuel}</td>
                    <td className="px-3 py-2 text-center text-slate-300">{r.annees_service_actuelles}a</td>
                    <td className="px-3 py-2 text-slate-400 font-mono whitespace-nowrap">{r.date_retraite_prevue}</td>
                    <td className="px-3 py-2 text-center"><span className={r.depart_retraite_dans_n_ans<=2?'text-red-400 font-bold':r.depart_retraite_dans_n_ans<=5?'text-orange-400':'text-green-400'}>{r.depart_retraite_dans_n_ans}a</span></td>
                    <td className="px-3 py-2 text-center text-blue-300 font-bold">{r.effectif_corps_region_actuel}</td>
                    <td className="px-3 py-2 text-center text-orange-400">{r.departs_retraite_prevus_5ans}</td>
                    <td className="px-3 py-2 text-center text-green-400">{r.besoins_recrutement_5ans}</td>
                    <td className="px-3 py-2 text-center text-purple-400">{r.promotions_previsibles_5ans}</td>
                    <td className="px-3 py-2 text-center"><span className={r.effectif_projete_2030<r.effectif_corps_region_actuel*0.85?'text-red-400 font-bold':'text-green-400'}>{r.effectif_projete_2030}</span></td>
                    <td className="px-3 py-2 text-right text-blue-300">{Math.round(r.cout_masse_salariale_actuelle_fcfa/1e6)}M</td>
                    <td className="px-3 py-2 text-right text-green-400">{Math.round(r.cout_projete_2030_fcfa/1e6)}M</td>
                    <td className="px-3 py-2 text-center">
                      <motion.span className={`px-1.5 py-0.5 rounded text-xs font-bold ${r.deficit_critique==='OUI'?'text-red-400 bg-red-500/10':'text-green-400 bg-green-500/10'}`}
                        animate={r.deficit_critique==='OUI'?{opacity:[1,.6,1]}:{}} transition={{ duration:1.5, repeat:Infinity }}>
                        {r.deficit_critique==='OUI'?'CRITIQUE':'OK'}
                      </motion.span>
                    </td>
                    <td className="px-3 py-2 text-center"><span className={`text-xs font-bold ${r.priorite_recrutement==='CRITIQUE'?'text-red-400':r.priorite_recrutement==='NORMALE'?'text-blue-400':'text-slate-500'}`}>{r.priorite_recrutement}</span></td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT
// ══════════════════════════════════════════════════════════════════════════════
export const HRAdvancedTabs = () => {
  const [tab, setTab] = useState<'rapprochement'|'fictifs'|'gpec'>('rapprochement');

  const TABS = [
    { k:'rapprochement', label:'Triple Rapprochement Paie',   icon:CheckCircle, accent:'text-green-400' },
    { k:'fictifs',       label:'Détection Agents Fictifs ML', icon:Brain,       accent:'text-purple-400' },
    { k:'gpec',          label:'GPEC 5 ans',                  icon:Calendar,    accent:'text-cyan-400' },
  ];

  return (
    <div className="mt-6 space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-blue-900/40"/>
        <span className="text-slate-400 text-xs font-medium px-3">Modules avancés — Intelligence RH · Google Sheets live</span>
        <div className="flex-1 h-px bg-blue-900/40"/>
      </div>

      <div className="flex items-center gap-1 rounded-xl p-1 flex-wrap"
        style={{ background:'linear-gradient(135deg,rgba(30,41,59,.8) 0%,rgba(15,23,42,.6) 100%)', backdropFilter:'blur(8px)', border:'1px solid rgba(59,130,246,.15)' }}>
        {TABS.map(t => (
          <motion.button key={t.k} onClick={() => setTab(t.k as any)}
            whileHover={{ scale:1.03 }} whileTap={{ scale:.97 }}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all ${tab===t.k?'bg-blue-600 text-white shadow-lg':'text-slate-400 hover:text-white'}`}>
            <t.icon className="w-3.5 h-3.5"/>{t.label}
          </motion.button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab==='rapprochement' && (
          <motion.div key="r" initial={{ opacity:0,y:12 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0,y:-12 }} transition={{ duration:.3 }}>
            <TripleRapprochement/>
          </motion.div>
        )}
        {tab==='fictifs' && (
          <motion.div key="f" initial={{ opacity:0,y:12 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0,y:-12 }} transition={{ duration:.3 }}>
            <AgentsFictifs/>
          </motion.div>
        )}
        {tab==='gpec' && (
          <motion.div key="g" initial={{ opacity:0,y:12 }} animate={{ opacity:1,y:0 }} exit={{ opacity:0,y:-12 }} transition={{ duration:.3 }}>
            <GpecSimulation/>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
